import _, { isNil } from 'lodash';
import { LineItem } from '../../types';
import { description as productDescriptionFn } from '../product/description';
import PurchaseOrderStatuses, { PurchaseOrderStatus } from '../purchaseOrder/Statuses';
import SalesOrderStatuses, { SalesOrderStatus } from '../salesOrder/Statuses';
import type { LineItemEffectiveQuantityInput } from './types';

type MergableLineItem = Partial<LineItem> & { id: number };

/**
 * Merges the appropriate child line item metrics into their parent line items.
 *
 * This is mostly used for Sales Orders, because multiple lots can be picked for a single parent line item.
 * @param lineItems all line items (including parents)
 *
 * See also:
 *   nestForGrid       - returns nested parent + children for grid rendering
 *   aggregateForTotals - returns parents with summed picked for metrics calc
 */
export const flattenForDisplay = (lineItems: MergableLineItem[], skipParentFilter = false): MergableLineItem[] => {
  // make sure parents get eval'd first.
  const sortedLineItems = [...lineItems].sort((a, b) => {
    if (a.parentLineItemId && !b.parentLineItemId) {
      return 1;
    }

    if (!a.parentLineItemId && b.parentLineItemId) {
      return -1;
    }

    return 0;
  });

  const parentLineItems: Record<number, any> = {};

  sortedLineItems.forEach((lineItem) => {
    if (!isNil(lineItem.parentLineItemId)) {
      // Its a child line item
      const parentItem = parentLineItems[lineItem.parentLineItemId];

      if (parentItem) {
        // Parent has been processed before. Add metrics.
        parentLineItems[lineItem.parentLineItemId] = {
          ...parentItem,
          childrenLineItems: [...(parentItem.childrenLineItems || []), lineItem],
          profit: (parentItem.profit || 0) + (lineItem.profit || 0),
          unitsPicked: (parentItem.unitsPicked || 0) + (lineItem.unitsPicked || 0),
          weightPicked: (parentItem.weightPicked || 0) + (lineItem.weightPicked || 0),
          lots: lineItem.lot
            ? [
                ...parentItem.lots,
                { ...lineItem.lot, unitsPicked: lineItem.unitsPicked || 0, weightPicked: lineItem.weightPicked },
              ]
            : parentItem.lots,
        };
      } else {
        // Parent has not been processed before. Add it to the parentLineItems object.
        parentLineItems[lineItem.parentLineItemId] = {
          ...lineItem,
          childrenLineItems: [lineItem],
          lots: lineItem.lot
            ? [{ ...lineItem.lot, unitsPicked: lineItem.unitsPicked || 0, weightPicked: lineItem.weightPicked }]
            : [],
        };
      }
    } else {
      // Its a parent line item
      parentLineItems[lineItem.id] = {
        ...lineItem,
        childrenLineItems: [...(lineItem.childrenLineItems || []), lineItem],
        lots: lineItem.lot
          ? [{ ...lineItem.lot, unitsPicked: lineItem.unitsPicked || 0, weightPicked: lineItem.weightPicked }]
          : [],
      };
    }
  });

  // `typeof item.id === 'string'` is used to see if grid has any auto-generated rows (draft rows)
  // draft rows have uuid.v4() as their id, db rows have an integer id
  return Object.values(parentLineItems).filter(
    (item) => skipParentFilter || isNil(item.parentLineItemId) || typeof item.id === 'string'
  );
};

/**
 * Inserts child line items into their parents — for the new grids, where the parent does not own its own lot/unitsPicked.
 * See also: `flattenForDisplay` (merged list for UI display), `aggregateForTotals` (parents with summed picked, for metrics).
 */
export const nestForGrid = (lineItems: LineItem[]): Partial<LineItem>[] => {
  const parentIdToChildren = _.groupBy(
    lineItems.filter((li) => li.parentLineItemId),
    (lineItem) => lineItem.parentLineItemId
  );
  const parents = lineItems.filter((lineItem) => isNil(lineItem.parentLineItemId));
  const parentIds = Object.keys(parentIdToChildren);
  const rowsWithoutChildren = parents.filter((lineItem) => !parentIds.includes(String(lineItem.id)));

  const groupedLineItems = parentIds.map((parentId) => {
    const children = parentIdToChildren[parentId];
    const unitsPicked = children.reduce((acc, child) => acc + (child.unitsPicked || 0), 0);
    const weightPicked = children.reduce((acc, child) => acc + (child.weightPicked || 0), 0);

    // if only a list of children are provided, still make sure the product is present.
    // Be advised that ordered units/weight, and pricing will be undefined.
    const fallbackToChild = { id: Number(parentId), product: children[0]?.product, childrenLineItems: [] };
    const parent = parents.find((lineItem) => lineItem.id === Number(parentId)) ?? fallbackToChild;

    return { ...parent, unitsPicked, weightPicked, childrenLineItems: children };
  });

  return [...rowsWithoutChildren, ...groupedLineItems].sort((a, b) => a.id - b.id);
};

type AggregableForTotals = {
  id: number;
  parentLineItemId?: number | null;
  unitsPicked?: number | null;
  weightPicked?: number | null;
};

export function aggregateForTotals<T extends AggregableForTotals>(lineItems: T[]): T[] {
  const parents = lineItems.filter((li) => !li.parentLineItemId);
  return parents.map((p) => {
    const kids = lineItems.filter((c) => c.parentLineItemId === p.id);
    if (kids.length === 0) return p;
    return {
      ...p,
      unitsPicked: kids.reduce((sum, c) => sum + (c.unitsPicked || 0), 0),
      weightPicked: kids.reduce((sum, c) => sum + (c.weightPicked || 0), 0),
    };
  });
}

/*
  The children may or may not have prices set; but in truth their parent line items have the only valid prices.
* */
export const getEffectiveLineItems = (lineItems: LineItem[]): LineItem[] => {
  const lineItemsById = _.keyBy(lineItems, (lineItem) => lineItem.id);

  return lineItems.map((lineItem) => {
    const parent = lineItem.parentLineItemId ? lineItemsById[lineItem.parentLineItemId] : null;

    if (!parent) {
      return lineItem;
    }

    let unsetFields = {};

    if (parent) {
      unsetFields = { unitsOrdered: undefined, weightOrdered: undefined };
    }

    return {
      ...lineItem,
      ...unsetFields,
      enteredByWeight: parent.enteredByWeight ?? false,
      sellPrice: parent.sellPrice,
      deliveredPrice: parent.deliveredPrice,
      sellCurrencyPrice: parent.sellCurrencyPrice,
      buyCurrencyPrice: parent.buyCurrencyPrice,
      buyPrice: parent.buyPrice,
      deliveredCurrencyPrice: parent.deliveredCurrencyPrice,
    };
  });
};

export function getEffectiveUnitsForStatus(
  lineItem: LineItemEffectiveQuantityInput,
  status: SalesOrderStatus | PurchaseOrderStatus | undefined
): number | null | undefined {
  if (lineItem.external) {
    return status === 'DRAFT' ? lineItem.unitsOrdered : lineItem.unitsShipped;
  }

  if (lineItem.salesOrderId) {
    if (SalesOrderStatuses.Groups.toSubmittedOrCancelled.includes(status as SalesOrderStatus)) {
      return lineItem.unitsOrdered;
    }

    return lineItem.unitsPicked;
  }

  if (lineItem.purchaseOrderId) {
    if (PurchaseOrderStatuses.Groups.toSubmittedOrCancelled.includes(status as PurchaseOrderStatus)) {
      return lineItem.unitsOrdered;
    }

    return lineItem.unitsReceived;
  }
}

export function getEffectiveWeightForStatus(
  lineItem: LineItemEffectiveQuantityInput,
  status: SalesOrderStatus | PurchaseOrderStatus | undefined
): number | null | undefined {
  if (lineItem.external) {
    return status === 'DRAFT' ? lineItem.weightOrdered : lineItem.weightShipped;
  }

  if (lineItem.salesOrderId) {
    if (SalesOrderStatuses.Groups.toSubmittedOrCancelled.includes(status as SalesOrderStatus)) {
      return lineItem.weightOrdered;
    }

    return lineItem.weightPicked;
  }

  if (lineItem.purchaseOrderId) {
    if (PurchaseOrderStatuses.Groups.toSubmittedOrCancelled.includes(status as PurchaseOrderStatus)) {
      return lineItem.weightOrdered;
    }

    return lineItem.weightReceived;
  }
}

/**
 * Get the product description for a line item.
 * If the line item has no product, returns freeformProductDescription or empty string.
 */
export function productDescription(lineItem: Partial<LineItem>, descriptionTemplate?: string | null): string {
  if (lineItem.product) {
    return productDescriptionFn(lineItem.product, descriptionTemplate);
  }

  return lineItem.freeformProductDescription || '';
}
